"use client";

import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Moon, Sun, Menu, X, ChevronRight } from "lucide-react";
import Footer from "../../components/layout/footer";
import Header from "../../components/layout/header";
import dynamic from "next/dynamic";



const API_BASE = process.env.NEXT_PUBLIC_API_BASE || "http://localhost:8000";

const tw = {
  primaryBg: "bg-[#003B5C]",
  primaryText: "text-[#003B5C]",
  tealBg: "bg-[#00778B]",
  tealText: "text-[#00778B]",
  goldBg: "bg-[#E3A130]",
  goldText: "text-[#E3A130]",
  grayText: "text-[#C7C9C7]",
};

const NEWS_PER_PAGE = 6;

// Helper function to normalize image URLs
function getFullImageUrl(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  if (url.startsWith("/uploads")) return `${API_BASE}${url}`;
  if (url.startsWith("/")) return `${API_BASE}/uploads${url}`;
  return `${API_BASE}/uploads/${url}`;
}

// Function to safely render HTML content
const renderHTML = (html) => {
  return { __html: html || "" };
};
const stripHtml = (html) => {
  if (!html) return "";
  return html.replace(/<[^>]*>?/gm, '');
};
export default function News() {
  // UI states
  const [darkMode, setDarkMode] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);

  // Hero slider state
  const [heroIndex, setHeroIndex] = useState(0);
  const heroIntervalRef = useRef(null);
  const [heroBanners, setHeroBanners] = useState([]);

  // News data
  const [newsList, setNewsList] = useState([]);
  const [loadingNews, setLoadingNews] = useState(false);
  const [newsError, setNewsError] = useState("");

  // Pagination
  const [currentNewsPage, setCurrentNewsPage] = useState(1);
  const [copiedNews, setCopiedNews] = useState(false);

  // Fetch news list
  useEffect(() => {
    async function fetchNews() {
      setLoadingNews(true);
      setNewsError("");
      try {
        const res = await fetch(`${API_BASE}/news/public`);
        if (!res.ok) throw new Error("Failed to fetch news");
        const data = await res.json();
        console.log("Fetched news data:", data);
        // Normalize images URLs and content
        const processedData = data.map((item) => ({
          ...item,
          images: item.images?.map((img) => ({
            ...img,
            url: getFullImageUrl(img.url),
          })) || [],
          // Ensure content is properly formatted
          content: item.content || "",
          excerpt: item.excerpt || (item.content ? item.content.substring(0, 160) + "..." : ""),
        }));

        setNewsList(processedData);

        // Create hero banners from news with images
        const banners = processedData
          .filter(item => item.images && item.images.length > 0)
          .slice(0, 5) // Limit to 5 featured news
          .map((item, index) => ({
            id: item.id,
            title: item.title_en,
            subtitle: stripHtml(item.excerpt_en || item.content_en?.substring(0, 100) + "..."), // Strip HTML here
            image: item.images[0].url,
            cta: "Read More",
            newsId: item.id
          }));

        // Fallback if no news with images
        if (banners.length === 0) {
          banners.push({
            id: 1,
            title: "Latest News Highlights",
            subtitle: "Stay updated with the freshest news",
            image: "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=1600&q=80",
            cta: "Explore",
          });
        }

        setHeroBanners(banners);
      } catch (error) {
        setNewsError(error.message);
      } finally {
        setLoadingNews(false);
      }
    }
    fetchNews();
  }, []);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Hero slider auto rotate
  useEffect(() => {
    if (heroBanners.length > 1) {
      heroIntervalRef.current = setInterval(() => {
        setHeroIndex((prev) => (prev + 1) % heroBanners.length);
      }, 7000);
      return () => clearInterval(heroIntervalRef.current);
    }
  }, [heroBanners]);

  // Build all news meta for copy
  const allNewsPlain = newsList
    .map(
      (n) =>
        [
          `ID: ${n.id}`,
          `Title: ${n.title_en}`,
          `Date: ${n.date}`,
          `URL: ${typeof window !== "undefined" ? window.location.origin : ""}/news/${n.id}`,
          `Excerpt: ${n.excerpt_en}`,
        ].join("\n")
    )
    .join("\n---\n");

  // Pagination slice
  const totalNewsPages = Math.ceil(newsList.length / NEWS_PER_PAGE);
  const pagedNewsList = newsList.slice(
    (currentNewsPage - 1) * NEWS_PER_PAGE,
    currentNewsPage * NEWS_PER_PAGE
  );

  if (!mounted) return null;

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-dark-gray text-zinc-900 dark:text-gray-text transition-colors">
      <Header />
      
      {/* Hero Banner Slider */}
      {heroBanners.length > 0 && (
        <section className="relative h-[60vh] sm:h-[70vh] w-full overflow-hidden">
          {/* Slides */}
          <div className="relative w-full h-full">
            {heroBanners.map((banner, index) => (
              <motion.div
                key={banner.id}
                initial={{ opacity: 0 }}
                animate={{ 
                  opacity: index === heroIndex ? 1 : 0,
                  zIndex: index === heroIndex ? 1 : 0
                }}
                transition={{ duration: 0.8 }}
                className={`absolute inset-0 w-full h-full ${index === heroIndex ? 'block' : 'hidden'}`}
              >
                <div 
                  className="w-full h-full bg-cover bg-center"
                  style={{ backgroundImage: `url(${banner.image})` }}
                >
                  <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center text-center px-6 sm:px-12 lg:px-20">
                    <motion.div
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.3 }}
                      className="max-w-2xl text-white mx-auto"
                    >
                      <h1 className="text-3xl sm:text-5xl font-bold leading-tight mb-4">
                        {banner.title_en}
                      </h1>
                      <p className="text-lg sm:text-xl mb-6">{banner.subtitle_en}</p>
                      <a
                        href={`/news/${banner.newsId || ''}`}
                        className={`inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium ${tw.goldBg} text-black hover:opacity-90 transition`}
                      >
                        {banner.cta} <ChevronRight className="w-4 h-4" />
                      </a>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Navigation Dots */}
          {heroBanners.length > 1 && (
            <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-2 z-10">
              {heroBanners.map((_, index) => (
                <button
                  key={`hero-dot-${index}`}
                  onClick={() => setHeroIndex(index)}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    heroIndex === index ? 'bg-white' : 'bg-white/50'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {/* News Section */}
      <section id="news" className="py-16 sm:py-24 bg-[#f7fafc] dark:bg-dark-gray">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-10 flex-wrap gap-3">
            <h2 className="text-2xl sm:text-3xl font-semibold text-[#003B5C] dark:text-white">
              News
            </h2>
            {/* <button
              onClick={() => {
                navigator.clipboard.writeText(allNewsPlain);
                setCopiedNews(true);
                setTimeout(() => setCopiedNews(false), 2000);
              }}
              className="text-xs px-3 py-1 rounded bg-zinc-200 dark:bg-zinc-800 hover:opacity-90"
            >
              {copiedNews ? "Copied!" : "Copy News Meta"}
            </button> */}
          </div>

          {loadingNews ? (
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#003B5C]"></div>
            </div>
          ) : newsError ? (
            <p className="text-center text-red-600">{newsError}</p>
          ) : newsList.length === 0 ? (
            <p className="text-center text-gray-500">No news available.</p>
          ) : (
            <>
              {/* News grid */}
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {pagedNewsList.map((item) => (
                  <div
                    key={item.id}
                    className="bg-white dark:bg-gray-accent rounded-xl shadow-md p-6 flex flex-col gap-3 border border-gray-100 dark:border-dark-gray-2 hover:shadow-lg transition-shadow"
                  >
                    {/* Image */}
                    {item.images && item.images.length > 0 ? (
                      <img
                        src={item.images[0].url}
                        alt={item.title_en}
                        className="rounded-lg w-full h-40 object-cover mb-3"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-40 bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-400 rounded-lg mb-3">
                        No Image
                      </div>
                    )}

                    <h3 className="text-lg font-bold text-[#003B5C] dark:text-white mb-1">
                      <a href={`/news/${item.id}`} className="hover:underline">
                        {item.title_en}
                      </a>
                    </h3>
                    
                    {/* Excerpt with HTML content */}
                    <div 
                      className="text-sm text-gray-600 dark:text-gray-300 mb-2 line-clamp-3"
                      dangerouslySetInnerHTML={renderHTML(item.excerpt_en || item.content_en?.substring(0, 200) + "...")}
                    />
                    

                    <a
                      href={`/news/${item.id}`}
                      className="mt-2 text-xs text-[#00778B] dark:text-[#5CA5D1] font-semibold hover:underline self-start"
                    >
                      Read More
                    </a>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              <div className="flex justify-center mt-8 space-x-2">
                {Array.from({ length: totalNewsPages }, (_, i) => i + 1).map(
                  (page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentNewsPage(page)}
                      className={`px-3 py-1 rounded ${
                        page === currentNewsPage
                          ? `${tw.primaryBg} text-white`
                          : "bg-zinc-200 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-300 dark:hover:bg-zinc-700"
                      }`}
                      aria-current={page === currentNewsPage ? "page" : undefined}
                    >
                      {page}
                    </button>
                  )
                )}
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}